import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Shield, House } from "@phosphor-icons/react";

import GeoInsightsDashboard   from "./geo/GeoInsightsDashboard";
import AnalyticsTab           from "./tabs/AnalyticsTab";
import PaymentsAnalyticsTab   from "./tabs/PaymentsAnalyticsTab";
import SettingsTab            from "./tabs/SettingsTab";
import UsersTab               from "./tabs/UsersTab";
import ConfigTab              from "./tabs/ConfigTab";
import CustomModelsTab        from "./tabs/CustomModelsTab";
// import PricingTab             from "./tabs/PricingTab"; // HIDDEN: re-enable when ready
import PaymentConfigTab       from "./tabs/PaymentConfigTab";
import EmailConfigTab         from "./tabs/EmailConfigTab";
import SocialLinksTab         from "./tabs/SocialLinksTab";
import BrandingTab            from "./tabs/BrandingTab";
import SeoStudioAdminTab      from "./tabs/SeoStudioAdminTab";
import AIJobsTab              from "./tabs/AIJobsTab";

// ── Navigation config ────────────────────────────────────────────────────────
import {
  ChartLine, GlobeHemisphereWest, Receipt, /* CurrencyDollar, */ CreditCard, // CurrencyDollar HIDDEN with Pricing tab
  Users, MagicWand, Image as ImageIcon, ShareNetwork,
  Database, Gear, Key, Plus, Envelope,
} from "@phosphor-icons/react";

const NAV_GROUPS = [
  {
    label: "Analytics",
    items: [
      { id: "analytics",    label: "Overview",     icon: ChartLine },
      { id: "geo-insights", label: "Geo Insights",  icon: GlobeHemisphereWest },
    ],
  },
  {
    label: "Payments",
    items: [
      { id: "payments-analytics", label: "Analytics", icon: Receipt },
      // { id: "pricing",            label: "Pricing",   icon: CurrencyDollar }, // HIDDEN: re-enable when ready
      { id: "payment-config",     label: "Config",    icon: CreditCard },
    ],
  },
  {
    label: "Users",
    items: [
      { id: "users", label: "Users", icon: Users },
    ],
  },
  {
    label: "Content",
    items: [
      { id: "seo-studio",   label: "SEO Studio", icon: MagicWand },
      { id: "branding",     label: "Branding",   icon: ImageIcon },
      { id: "social-links", label: "Follow Us",  icon: ShareNetwork },
    ],
  },
  {
    label: "AI Data",
    items: [
      { id: "ai-jobs", label: "Data Jobs", icon: Database },
    ],
  },
  {
    label: "Configuration",
    items: [
      { id: "settings",      label: "Settings",      icon: Gear },
      { id: "config",        label: "Models & Keys",  icon: Key },
      { id: "custom-models", label: "Custom Models",  icon: Plus },
      { id: "email-config",  label: "Email Config",   icon: Envelope },
    ],
  },
];

const TABS_FLAT = NAV_GROUPS.flatMap((g) => g.items);

function useIsLg() {
  const [isLg, setIsLg] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsLg(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isLg;
}

// ── Tab renderer ─────────────────────────────────────────────────────────────
function TabContent({ tab, refreshSettings, currentUserId }) {
  switch (tab) {
    case "analytics":          return <AnalyticsTab />;
    case "geo-insights":       return <GeoInsightsDashboard />;
    case "payments-analytics": return <PaymentsAnalyticsTab />;
    case "settings":           return <SettingsTab onSaved={refreshSettings} />;
    case "seo-studio":         return <SeoStudioAdminTab />;
    case "branding":           return <BrandingTab onSaved={refreshSettings} />;
    // case "pricing":            return <PricingTab />; // HIDDEN: re-enable when ready
    case "payment-config":     return <PaymentConfigTab />;
    case "users":              return <UsersTab currentUserId={currentUserId} />;
    case "config":             return <ConfigTab onSaved={refreshSettings} />;
    case "custom-models":      return <CustomModelsTab />;
    case "email-config":       return <EmailConfigTab />;
    case "social-links":       return <SocialLinksTab />;
    case "ai-jobs":            return <AIJobsTab />;
    default:                   return null;
  }
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminPage() {
  const { user, refreshSettings } = useAuth();
  const [tab, setTab] = useState("analytics");
  const isLg = useIsLg();

  const activeLabel = TABS_FLAT.find((t) => t.id === tab)?.label ?? "Dashboard";

  return (
    <div className="flex w-full h-screen bg-slate-50 text-slate-900" data-testid="admin-page">

      {/* ── Sidebar (lg+) ───────────────────────────────────────── */}
      {isLg && (
        <aside
          className="flex flex-col flex-shrink-0 w-60 xl:w-64 border-r border-slate-800 bg-slate-900 overflow-y-auto"
          data-testid="admin-sidebar"
        >
          {/* Sidebar header */}
          <div className="px-4 py-5 border-b border-slate-800 shrink-0">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0">
                <Shield size={13} weight="fill" className="text-white" />
              </div>
              <span className="font-semibold text-white text-sm">Admin Panel</span>
            </div>
            <p className="text-xs text-slate-500 truncate pl-0.5">{user?.email}</p>
          </div>

          {/* Grouped navigation */}
          <nav className="flex flex-col py-3 px-3 gap-0.5 flex-1" data-testid="admin-tabs">
            {NAV_GROUPS.map((group) => (
              <div key={group.label} className="mb-4">
                <div className="px-2 pb-1.5 pt-1">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    {group.label}
                  </span>
                </div>
                {group.items.map((t) => {
                  const Icon = t.icon;
                  const active = tab === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      className={`flex items-center gap-2.5 w-full px-3 py-2 text-left text-sm rounded-lg transition-colors mb-0.5 ${
                        active
                          ? "bg-indigo-600 text-white"
                          : "text-slate-400 hover:text-white hover:bg-slate-800/70"
                      }`}
                      data-testid={`admin-tab-${t.id}`}
                    >
                      <Icon size={15} weight={active ? "fill" : "regular"} />
                      <span className="truncate font-medium">{t.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Back to site */}
          <div className="px-3 py-4 border-t border-slate-800 shrink-0">
            <Link
              to="/"
              className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/70 rounded-lg transition-colors"
            >
              <House size={15} weight="fill" />
              Back to site
            </Link>
          </div>
        </aside>
      )}

      {/* ── Content area ────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden flex flex-col">

        {/* Mobile tab strip (hidden lg+) */}
        {!isLg && (
          <div
            className="flex shrink-0 border-b border-slate-200 overflow-x-auto bg-white shadow-sm"
            data-testid="admin-tabs"
          >
            {TABS_FLAT.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex shrink-0 items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 whitespace-nowrap transition-colors ${
                    active
                      ? "border-indigo-600 text-indigo-600"
                      : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                  data-testid={`admin-tab-${t.id}`}
                >
                  <Icon size={12} weight="bold" /> {t.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Page top-bar */}
        <div className="bg-white border-b border-slate-200 px-6 xl:px-8 py-4 shrink-0">
          <div className="flex items-center gap-2 mb-0.5">
            <Shield size={12} weight="fill" className="text-indigo-600" />
            <span className="text-xs font-medium text-slate-400">Admin · {user?.email}</span>
          </div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">{activeLabel}</h1>
        </div>

        {/* Tab content */}
        <div className="px-6 xl:px-8 py-6 min-w-0">
          <TabContent tab={tab} refreshSettings={refreshSettings} currentUserId={user?.user_id} />
        </div>
      </div>
    </div>
  );
}
